



































import { Component, Vue } from 'vue-property-decorator';

import SpecialCoinOffersService from '@/api/SpecialCoinOffersService';

import { SPECIAL_OFFER_PREVIEW_STORAGE_KEY } from '@/store/modules/offer';

import { EStartGuideTaskSlug } from '@/models/start-guide';
import { IBusinessType } from '@/models/business';

import isMobile from '@/utils/mobile-check';
import { updateRegistration } from '@/utils/updateRegistration';
import eventBus from '@/utils/eventBus';
import { handleCompleteTask } from '@/utils/start-guide';
import segmentIo from '@/utils/segment.io';

import { Surface, Picture, Typography } from '@/components/atoms';
import { FilledButton } from '@/components/molecules';
import Page from '@/componentsV2/molecules/Page/Page.vue';
import {
  ESpecialCoinOfferType,
  SpecialOfferTypeEnum,
  transformOldOfferTypeToNew,
  TSpecialCoinOfferType
} from '@/models/offer';
import { BreadcrumbData } from '@/componentsV2/molecules/Breadcrumbs/Breadcrumbs.vue';
import { isRemoteLoading } from '@/utils/remote-loading';

import ChangeType from './Steps/ChangeType.vue';
import FormOffer from './Steps/FormOffer/FormOffer.vue';
import ShareOffer from './Steps/ShareOffer.vue';

export enum EOfferCreationStepSlug {
  CHANGE_TYPE = 'change-type',
  FORM_OFFER = 'form-offer',
  SHARE_OFFER = 'share-offer'
}

@Component({
  components: {
    Surface,
    Picture,
    Typography,
    FilledButton,
    Page,
    ChangeType,
    FormOffer,
    ShareOffer
  }
})
export default class OfferCreation extends Vue {
  loading = false;
  type = '';
  errors = [];
  offerData: any = {};
  activeStepSlug = EOfferCreationStepSlug.CHANGE_TYPE;
  lengthListBirthday = 0;
  EOfferCreationStepSlug = EOfferCreationStepSlug;

  service = new SpecialCoinOffersService();

  get tPrefix() {
    return `offers`;
  }

  get componentByStepSlug(): {
    [k: string]: {
      component: string;
    };
  } {
    return {
      [EOfferCreationStepSlug.CHANGE_TYPE]: {
        component: 'ChangeType'
      },
      [EOfferCreationStepSlug.FORM_OFFER]: {
        component: 'FormOffer'
      },
      [EOfferCreationStepSlug.SHARE_OFFER]: {
        component: 'ShareOffer'
      }
    };
  }

  get remoteLoading() {
    return isRemoteLoading();
  }

  get isMobileDevice() {
    return isMobile();
  }

  get flags() {
    return this.$store.getters['flags/getFlags'];
  }

  get breadcrumbs() {
    if (this.activeStepSlug === EOfferCreationStepSlug.SHARE_OFFER) return '';
    const breadcrumbList: BreadcrumbData[] = [
      {
        label: this.$t(`${this.tPrefix}.breadcrumbs`) as string,
        path: '/special-coins-offers'
      },
      {
        label: this.$t(`${this.tPrefix}.breadcrumbsSelect`) as string
      }
    ];

    if (this.activeStepSlug === EOfferCreationStepSlug.FORM_OFFER) {
      breadcrumbList[breadcrumbList.length - 1].onClick = () => {
        this.activeStepSlug = EOfferCreationStepSlug.CHANGE_TYPE;
      };
      const label = this.$t(
        `${this.tPrefix}.breadcrumbsCreate.${
          this.isMobileDevice ? 'mobile' : 'desktop'
        }.${this.type || this.$route.query.type}`
      ) as string;
      breadcrumbList.push({
        label
      });
    }

    return breadcrumbList;
  }

  get titleMobile() {
    if (this.activeStepSlug === EOfferCreationStepSlug.FORM_OFFER)
      return this.$t(`${this.tPrefix}.breadcrumbsCreate.${this.type}`);
    return `Select an ${this.$t(`${this.tPrefix}.title.singular`)} type`;
  }

  get businessCurrency() {
    return this.$store.getters['internationalization/getCountrySettings']
      .currencySymbol;
  }

  get isReferralOfferActive() {
    return this.flags.includes('referral-offer');
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'] || {};
  }

  get availableTypes(): string[] {
    return [
      ...Object.values(SpecialOfferTypeEnum),
      ...Object.values(ESpecialCoinOfferType)
    ];
  }

  get birthdayIsDisabled() {
    return (
      this.businessInfo.country !== 'United States' ||
      this.businessInfo.type === IBusinessType.ONLINE
    );
  }

  mounted() {
    const queryType = String(this.$route.query.type);

    let type = queryType;

    const transformedType = transformOldOfferTypeToNew(
      queryType as ESpecialCoinOfferType
    );

    if (transformedType) {
      type = transformedType;
    }

    if (this.availableTypes.includes(queryType)) {
      this.type = type;
      this.activeStepSlug = EOfferCreationStepSlug.FORM_OFFER;

      return;
    }

    this.$router.push({
      path: '/special-coins-offers/create',
      query: this.$route.query
    });
  }

  addPageView(page: string) {
    segmentIo.page(page);
  }

  close() {
    if (this.activeStepSlug !== EOfferCreationStepSlug.CHANGE_TYPE) {
      this.activeStepSlug = EOfferCreationStepSlug.CHANGE_TYPE;
      this.scrollToTop();
      this.addPageView('Special Coin Offers/Creation/Change Type');
      return;
    }
    this.$router.push('/special-coins-offers');
  }

  changeType(value: TSpecialCoinOfferType) {
    this.type = value;
  }

  handleGoToStep({
    redirectToSlug
  }: {
    redirectToSlug: EOfferCreationStepSlug;
  }) {
    this.activeStepSlug = redirectToSlug;
  }

  scrollToTop() {
    const el = document.querySelector('.container-scrollable');
    if (el) el.scrollTo({ top: 0 });
  }

  async finish(data: Record<string, any>) {
    this.$showLoaderWithLocker();

    try {
      const res = await this.service.newOffer(data);
      if (res.status === 201) {
        this.addPageView('Special Coin Offers/Success');

        updateRegistration('create-your-first-offer');
        localStorage.removeItem(SPECIAL_OFFER_PREVIEW_STORAGE_KEY);

        handleCompleteTask(EStartGuideTaskSlug.SPECIAL_COINS);

        window.parent.postMessage(
          {
            type: 'offer:campaign-created',
            data: res.data
          },
          '*'
        );

        const params = this.$route.params;

        if (params.taskSlug) {
          this.$router.push('/home');

          return;
        }

        if (this.isReferralOfferActive) {
          await this.onSuccess(res.data.id);
        } else {
          this.close();
          this.$notification('Offer created');
          eventBus.$emit('reloadOfferList', true);
        }
      }
    } catch (e) {
      segmentIo.track('Error to Create Offer', { Error: e });
    } finally {
      this.$hideLoader();
    }
  }

  async onSuccess(id: string) {
    const { data } = await this.service.getOfferById(id);

    this.offerData = {
      ...data,
      picture: data.image.publicPath.large
    };
    this.activeStepSlug = EOfferCreationStepSlug.SHARE_OFFER;
    this.scrollToTop();
  }

  async created() {
    if (this.$route.query.offer) {
      this.offerData = JSON.parse(this.$route.query.offer as string);
    }
    this.loading = true;
    const { data } = await this.service.getOffers(
      '',
      '',
      '&status=active&limit=10&specialType=Birthday',
      ''
    );
    this.loading = false;

    this.lengthListBirthday = data.total;

    if (this.$route.query.type === 'birthday' && this.birthdayIsDisabled) {
      this.type = '';
      this.activeStepSlug = EOfferCreationStepSlug.CHANGE_TYPE;

      this.$router.push({
        path: '/special-coins-offers/create',
        query: this.$route.query
      });
    }
  }

  beforeDestroy() {
    localStorage.removeItem(SPECIAL_OFFER_PREVIEW_STORAGE_KEY);
  }
}
